import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { EmailAnalysisTabType } from "@types";
import { signOut } from "@store/auth";
import { getThreadTrackStatus, updateThreadTrackStatus } from "@store/signals";
import { SimulatorState } from "./simulator.types";
import { simulateDraft } from "./simulator.thunks";

const initialState: SimulatorState = {
  isLoading: {
    simulateDraft: false,
  },
  isError: {
    simulateDraft: false,
    permissionError: false,
    recipientsError: false,
  },
  activeTabId: EmailAnalysisTabType.Analysis,
  result: null,
  selectedBranch: null,
  selectedEmail: null,
  currentDraftState: null,
  threadId: null,
  threadTrackStatus: null,
};

const simulatorSlice = createSlice({
  initialState,
  name: "simulator",
  reducers: {
    selectBranch: (state, action: PayloadAction<string>) => {
      const selectedBranch =
        state.result?.branchResults?.find(({ branchId }) => branchId === action.payload) || null;
      const selectedBranchEmails = selectedBranch?.emails || [];

      state.selectedBranch = selectedBranch;
      state.selectedEmail = selectedBranch?.draft
        ? selectedBranch.draft || null
        : selectedBranchEmails[selectedBranchEmails.length - 1] || null;
    },
    selectEmail: (state, action: PayloadAction<string>) => {
      const isDraft = state.selectedBranch?.draft?.messageId === action.payload;
      state.selectedEmail = isDraft
        ? state.selectedBranch?.draft || null
        : state.selectedBranch?.emails?.find(({ messageId }) => messageId === action.payload) ||
          null;
    },
    switchSimulatorTab: (state, action: PayloadAction<EmailAnalysisTabType>) => {
      state.activeTabId = action.payload;
    },
    clearSimulationResult: (state) => {
      state.result = null;
      state.selectedBranch = null;
      state.selectedEmail = null;
    },
    updateThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(simulateDraft.pending, (state, action) => {
      const { draftText, subject, recipients } = action.meta.arg;
      state.isLoading.simulateDraft = true;
      state.isError.simulateDraft = false;
      state.isError.permissionError = false;
      state.isError.recipientsError = false;
      state.currentDraftState = JSON.stringify({
        draftText,
        subject,
        recipients,
      });
    });
    builder.addCase(simulateDraft.fulfilled, (state, action) => {
      const { messageId } = action.meta.arg;
      const branchResults = action.payload.branchResults || [];
      const selectedBranch =
        branchResults?.find(({ draft }) => draft?.platformDraftId === messageId) || null;
      const selectedEmail = selectedBranch?.draft || null;

      state.isLoading.simulateDraft = false;
      state.result = action.payload;
      state.selectedBranch = selectedBranch;
      state.selectedEmail = selectedEmail;
    });
    builder.addCase(simulateDraft.rejected, (state, action) => {
      const { permissionError = false, recipientsError = false } =
        (action.payload as { permissionError: boolean; recipientsError: boolean }) || {};
      state.isLoading.simulateDraft = false;
      state.isError.simulateDraft = !action.meta.aborted;
      state.isError.permissionError = permissionError;
      state.isError.recipientsError = recipientsError;
    });
    builder.addCase(getThreadTrackStatus.fulfilled, (state, action) => {
      state.threadTrackStatus =
        action.meta.arg.threadId === state.threadId ? action.payload.onTrack : null;
    });
    builder.addCase(updateThreadTrackStatus.pending, (state, action) => {
      state.threadTrackStatus =
        action.meta.arg.threadId === state.threadId ? action.meta.arg.onTrack : null;
    });
    builder.addCase(signOut.pending, (state) => {
      state.activeTabId = EmailAnalysisTabType.Analysis;
      state.result = null;
      state.selectedBranch = null;
      state.selectedEmail = null;
      state.currentDraftState = null;
      state.threadId = null;
      state.threadTrackStatus = null;
    });
  },
});

export const {
  selectBranch,
  selectEmail,
  switchSimulatorTab,
  clearSimulationResult,
  updateThreadId,
} = simulatorSlice.actions;

export const simulatorReducer = simulatorSlice.reducer;
